import React, { useState } from 'react';
import { PiCheckBold } from 'react-icons/pi';
import * as turf from '@turf/turf';

function transformCoordinates(coords: any, width: number, height: number, padding = 10) {
  let flatCoords = coords.flat(Infinity);
  let xs = flatCoords.filter((_: any, i: number) => i % 2 === 0);
  let ys = flatCoords.filter((_: any, i: number) => i % 2 === 1);

  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  const scaleX = (width - padding * 2) / (maxX - minX || 1);
  const scaleY = (height - padding * 2) / (maxY - minY || 1);

  const scale = Math.min(scaleX, scaleY);

  return coords.map((ring: any) =>
    ring.map(([x, y]: [number, number]) => [
      padding + (x - minX) * scale,
      height - (padding + (y - minY) * scale),
    ]),
  );
}

function formatNumber(num: number, decimals: number = 2): string {
  return num.toLocaleString('vi-VN', { maximumFractionDigits: decimals });
}

function formatArea(areaM2: number): string {
  if (areaM2 >= 1000000) {
    return `${formatNumber(areaM2 / 1000000, 2)} km²`;
  }
  return `${formatNumber(areaM2, 2)} m²`;
}

function formatLength(lengthM: number): string {
  if (lengthM >= 1000) {
    return `${formatNumber(lengthM / 1000, 2)} km`;
  }
  return `${formatNumber(lengthM, 2)} m`;
}

function getGeometryInfo(geom: any): { type: string; info: { label: string; value: string }[] } | null {
  if (!geom || !geom.type) return null;

  if (geom.type === 'Point') {
    const [lng, lat] = geom.coordinates;
    return {
      type: 'Point',
      info: [
        { label: 'Vĩ độ', value: formatNumber(lat, 6) },
        { label: 'Kinh độ', value: formatNumber(lng, 6) },
      ],
    };
  }

  if (geom.type === 'LineString') {
    const line = turf.lineString(geom.coordinates);
    const length = turf.length(line, { units: 'meters' });
    return {
      type: 'LineString',
      info: [{ label: 'Chiều dài', value: formatLength(length) }],
    };
  }

  if (geom.type === 'Polygon') {
    const polygon = turf.polygon(geom.coordinates);
    const area = turf.area(polygon);
    const perimeter = turf.length(turf.polygonToLine(polygon), { units: 'meters' });
    return {
      type: 'Polygon',
      info: [
        { label: 'Diện tích', value: formatArea(area) },
        { label: 'Chu vi', value: formatLength(perimeter) },
      ],
    };
  }

  if (geom.type === 'MultiPolygon') {
    let totalArea = 0;
    let totalPerimeter = 0;
    geom.coordinates.forEach((coords: any) => {
      const polygon = turf.polygon(coords);
      totalArea += turf.area(polygon);
      totalPerimeter += turf.length(turf.polygonToLine(polygon), { units: 'meters' });
    });
    return {
      type: 'MultiPolygon',
      info: [
        { label: 'Diện tích', value: formatArea(totalArea) },
        { label: 'Chu vi', value: formatLength(totalPerimeter) },
      ],
    };
  }

  if (geom.type === 'MultiLineString') {
    let totalLength = 0;
    geom.coordinates.forEach((coords: any) => {
      const line = turf.lineString(coords);
      totalLength += turf.length(line, { units: 'meters' });
    });
    return {
      type: 'MultiLineString',
      info: [{ label: 'Chiều dài', value: formatLength(totalLength) }],
    };
  }

  return null;
}

interface GeometryPreviewProps {
  geometry: any;
  width?: number;
  height?: number;
  showInfo?: boolean;
}

export default function GeometryPreview({
  geometry,
  width = 60,
  height = 60,
  showInfo = true,
}: GeometryPreviewProps) {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const handleCopy = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (!geometry) return <div>No geometry provided</div>;

  const geometryInfo = getGeometryInfo(geometry);

  const renderShape = (geom: any) => {
    if (geom.type === 'Point') {
      const [x, y] = transformCoordinates(
        [[geom.coordinates]],
        width,
        height,
      )[0][0];
      return <circle cx={x} cy={y} r={3} fill="#3b82f6" />;
    }

    if (geom.type === 'LineString') {
      const points = transformCoordinates([geom.coordinates], width, height)[0]
        .map(([x, y]: [number, number]) => `${x},${y}`)
        .join(' ');
      return (
        <polyline points={points} fill="none" stroke="#10b981" strokeWidth="1.5" />
      );
    }

    if (geom.type === 'Polygon') {
      const rings = transformCoordinates(geom.coordinates, width, height);
      return rings.map((ring: any, idx: number) => (
        <polygon
          key={idx}
          points={ring.map(([x, y]: [number, number]) => `${x},${y}`).join(' ')}
          fill={idx === 0 ? 'rgba(239, 68, 68, 0.3)' : 'rgba(255, 255, 255, 0.8)'}
          stroke="#ef4444"
          strokeWidth="1.5"
        />
      ));
    }

    if (geom.type === 'MultiPolygon') {
      return geom.coordinates.map((polygon: any, index: number) => (
        <g key={index}>
          {renderShape({ type: 'Polygon', coordinates: polygon })}
        </g>
      ));
    }

    if (geom.type === 'MultiLineString') {
      return geom.coordinates.map((line: any, index: number) => (
        <g key={index}>
          {renderShape({ type: 'LineString', coordinates: line })}
        </g>
      ));
    }

    return (
      <text x={10} y={20} fontSize="10" fill="#666">
        Unsupported: {geom.type}
      </text>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <svg
        width={width}
        height={height}
        style={{
          border: '1px solid #e2e8f0',
          borderRadius: '4px',
          background: '#fafafa',
        }}
      >
        {renderShape(geometry)}
      </svg>
      {showInfo && geometryInfo && geometryInfo.info.length > 0 && (
        <div style={{ fontSize: '10px', color: '#666', marginTop: '2px', textAlign: 'center', lineHeight: 1.3 }}>
          {geometryInfo.info.map((item, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', marginTop: idx > 0 ? '2px' : 0 }}>
              <span>{item.label}: {item.value}</span>
              <button
                onClick={() => handleCopy(item.value, idx)}
                style={{
                  padding: '1px 4px',
                  fontSize: '8px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '3px',
                  background: copiedIndex === idx ? '#52c41a' : '#fff',
                  color: copiedIndex === idx ? '#fff' : '#999',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                  lineHeight: 1,
                }}
                title={`Sao chép ${item.label.toLowerCase()}`}
              >
                {copiedIndex === idx ? <PiCheckBold size={9} /> : (
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                  </svg>
                )}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
